import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CurrencyProvider } from './context/CurrencyContext';
import { ThemeProvider } from './context/ThemeContext';
import { StrategyProvider } from './context/StrategyContext';
import { PortfolioProvider } from './context/PortfolioContext';
import { Header } from './components/Layout/Header';
import { Footer } from './components/Layout/Footer';
import { Dashboard } from './components/Dashboard/Dashboard';
import { HoldingsTable } from './components/Holdings/HoldingsTable';
import { TransactionList } from './components/Transactions/TransactionList';
import { PortfolioLineChart } from './components/Charts/PortfolioLineChart';
import { useWalletFilter } from './hooks/useWalletFilter';
import { AllocationPieChart } from './components/Charts/AllocationPieChart';
import { StrategyView } from './components/Strategy/StrategyView';
import { TaxView } from './components/Strategy/TaxView';
import { WalletAllocationChart } from './components/Charts/WalletAllocationChart';
import { CashOutGoalCard } from './components/Dashboard/CashOutGoalCard';
import { CoinChartPage } from './components/CoinChartPage/CoinChartPage';
import { SettingsPage } from './components/Settings/SettingsPage';
import { LandingPage } from './components/Landing/LandingPage';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider, useSubscription } from './hooks/useSubscription';
import { useUpgrade } from './hooks/useUpgrade';
import { consumePendingPlan } from './lib/pendingPlan';
import { CheckoutReturnNotice } from './components/Subscription/CheckoutReturnNotice';
import { UpgradePromptProvider, useUpgradePrompt } from './context/UpgradePromptContext';
import { AuthPage } from './components/Auth/AuthPage';
import { ResetPasswordPage } from './components/Auth/ResetPasswordPage';
import { RequireAuth } from './components/Auth/RequireAuth';
import { LegalPage } from './components/Legal/LegalPage';
import { useCoinPrices } from './hooks/useCoinPrices';
import { useMetalPrices } from './hooks/useMetalPrices';
import { useStockPrices } from './hooks/useStockPrices';
import { usePortfolio } from './hooks/usePortfolio';
import { Transaction, CoinListItem, Holding, PortfolioStats, PortfolioSnapshot, PriceMap } from './types';
import { METALS, isMetal } from './utils/metals';
import { isStockOrETF } from './utils/stocks';
import { MarketView } from './components/Market/MarketView';
import { ConnectedWalletsList } from './components/Wallet/ConnectedWalletsList';
import { useConnectedWallets } from './hooks/useConnectedWallets';
import { LayoutDashboard, ArrowLeftRight, Globe, Landmark, Receipt } from 'lucide-react';
import { SampleDataBanner } from './components/Layout/SampleDataBanner';
import { useSampleMode, isSampleEnabled } from './hooks/useSampleMode';
import { SAMPLE_TRANSACTIONS, SAMPLE_COIN_IDS } from './data/sampleData';
import { EmptyDashboard } from './components/Dashboard/EmptyDashboard';
import { AddTransactionModal } from './components/Transactions/TransactionList';
import { computeHoldings, computePortfolioStats, buildPortfolioHistory } from './utils/calculations';

/** Read portfolio coin IDs from localStorage synchronously so prices start fetching on first render */
function getStoredCoinIds(): string[] {
  try {
    const raw = localStorage.getItem('mycryptoplanner_transactions');
    const txs: Transaction[] = raw ? JSON.parse(raw) : [];
    return Array.from(new Set(txs.map((t) => t.coinId)));
  } catch {
    return [];
  }
}

/**
 * Scrolls the window to the top on every route change. React Router preserves
 * the previous page's scroll position across navigations by default, which made
 * Coin Detail pages open scrolled partway down. Rendered once inside the Router.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);
  return null;
}

type Tab = 'dashboard' | 'transactions' | 'strategy' | 'tax' | 'market';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard',    label: 'Portfolio',    icon: <LayoutDashboard className="w-4 h-4" /> },
  { id: 'transactions', label: 'Transactions', icon: <ArrowLeftRight className="w-4 h-4" /> },
  { id: 'strategy',     label: 'Strategy',     icon: <Landmark className="w-4 h-4" /> },
  { id: 'tax',          label: 'Tax',          icon: <Receipt className="w-4 h-4" /> },
  { id: 'market',       label: 'Market',       icon: <Globe className="w-4 h-4" /> },
];

/** Narrow an arbitrary string (e.g. from the ?tab= query param) to a known Tab id. */
const isTab = (value: unknown): value is Tab => TABS.some((t) => t.id === value);

// ── AppInner ──────────────────────────────────────────────────────────────────

interface AppInnerProps {
  holdings:          Holding[];
  transactions:      Transaction[];
  prices:            PriceMap;
  stats:             PortfolioStats;
  history:           PortfolioSnapshot[];
  addTransaction:    (tx: Omit<Transaction, 'id' | 'timestamp'>) => void;
  removeTransaction: (id: string) => void;
  updateTransaction: (id: string, updates: Omit<Transaction, 'id' | 'timestamp'>) => void;
  pricesLoading:     boolean;
  pricesError:       string | null;
  metalError:        string | null;
  lastUpdated:       Date | null;
  refresh:           () => void;
  metalCoins:        CoinListItem[];
  sampleActive:      boolean;
  onEnableSample:    () => void;
  onDismissSample:   () => void;
}

function AppInner({
  holdings, transactions, prices, stats, history,
  addTransaction, removeTransaction, updateTransaction,
  pricesLoading, pricesError, metalError, lastUpdated, refresh, metalCoins,
  sampleActive, onEnableSample, onDismissSample,
}: AppInnerProps) {
  const location      = useLocation();
  const navigate      = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationState = location.state as { scrollToTaxPlan?: boolean } | null;

  const { isSubscribed, canPlan, loading: subLoading } = useSubscription();
  const { open: openUpgradePrompt } = useUpgradePrompt();
  const { startUpgrade } = useUpgrade();

  // The Dashboard's wallet filter, shared by Holdings, Portfolio Value and Asset
  // Allocation. Filtering the Holdings TABLE is free (as it always was); scoping
  // the chart and allocation to one wallet is part of the multi-wallet breakdown,
  // which is Plus. On Free those two keep the whole portfolio and show a lock.
  const wallet = useWalletFilter(holdings, transactions, prices);
  const walletScoped = canPlan && wallet.walletLabel !== null && wallet.timelineWallet !== null;
  // Not while the tier is still loading, or a Plus user sees the lock flash.
  const walletLocked = !subLoading && !canPlan && wallet.walletLabel !== null ? wallet.walletLabel : null;
  // The selected wallet's live total: the sum of its Holdings rows (USD).
  const walletTotalUsd = useMemo(
    () => wallet.walletHoldings.reduce((s, h) => s + h.totalValue, 0),
    [wallet.walletHoldings]
  );

  // Landing → checkout bridge: if the user picked the ANNUAL plan on the landing
  // page's "Get Pro" button, that choice was stashed in localStorage (router
  // state can't survive the logged-out /login redirect). Now that they're
  // authenticated on /app, consume it once and start an annual checkout — unless
  // they're already Pro. Monthly / null / expired just lands them on the
  // dashboard. Waits for the subscription status so it never fires for a subscriber.
  // If the annual checkout can't redirect, fall back to the Upgrade prompt so the
  // user still has a visible way to finish upgrading.
  const pendingPlanHandledRef = useRef(false);
  useEffect(() => {
    if (pendingPlanHandledRef.current) return;
    if (subLoading) return; // wait until we know whether they're already Pro
    pendingPlanHandledRef.current = true;
    const plan = consumePendingPlan(); // one-shot read + clear
    if (plan && plan.period === 'annual' && !isSubscribed) {
      startUpgrade(plan.tier, 'annual').then((result) => {
        if (result.kind !== 'redirected') {
          // Checkout could not redirect. Fall back to the modal, preselecting the
          // tier they picked on the landing page so the choice is not lost.
          openUpgradePrompt({
            tier: plan.tier,
            message: 'Ready to upgrade? Finish setting up your subscription below.',
          });
        }
      });
    }
  }, [subLoading, isSubscribed, startUpgrade, openUpgradePrompt]);


  // The active main tab lives in the URL (?tab=…) so it survives a refresh and
  // works with browser back/forward. Missing or invalid → Portfolio (dashboard).
  const tabParam = searchParams.get('tab');
  const activeTab: Tab = isTab(tabParam) ? tabParam : 'dashboard';
  const setActiveTab = useCallback(
    (tab: Tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          next.delete('sub'); // sub-tab state is scoped to its main tab — reset on switch
          return next;
        },
        { replace: false } // push a history entry so back/forward steps through tabs
      );
    },
    [setSearchParams]
  );

  const { wallets: connectedWallets, removeWallet, updateWallet } = useConnectedWallets();

  // Zero-real-transactions empty state (only when sample mode is NOT opted into —
  // when it is, `transactions` is the sample set and this is false).
  const showEmptyState = !sampleActive && transactions.length === 0;
  const [addTxnOpen, setAddTxnOpen] = useState(false);
  const [goalOpenSignal, setGoalOpenSignal] = useState(0);
  const handleSetGoal = useCallback(() => {
    document.getElementById('cash-out-goal')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setGoalOpenSignal((n) => n + 1);
  }, []);

  // Arriving from a "View plan" / "Add to plan" link: open the Tax tab and scroll to
  // the Tax Plan section once it has rendered. The delay lets the tab mount and wins
  // over the global ScrollToTop (which resets to 0 on navigation). A ref guards
  // against re-running, and we defer clearing the history-state flag until after the
  // scroll so the deferred navigate doesn't cancel our timeout.
  const taxScrollDone = useRef(false);
  useEffect(() => {
    if (!locationState?.scrollToTaxPlan || taxScrollDone.current) return;
    taxScrollDone.current = true;
    // Safety net: the deep link carries ?tab=tax, but ensure the Tax tab is active
    // without adding a history entry.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'tax');
        return next;
      },
      { replace: true }
    );
    const t = setTimeout(() => {
      document.getElementById('tax-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Clear the one-shot flag so a refresh doesn't replay the scroll; keep ?tab=tax
      // so the tab itself still persists.
      navigate({ pathname: '/app', search: '?tab=tax' }, { replace: true, state: {} });
    }, 160);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.scrollToTaxPlan]);

  return (
    <div className="min-h-screen bg-slate-900">
      <CheckoutReturnNotice />

      <Header lastUpdated={lastUpdated} loading={pricesLoading} onRefresh={refresh} />

      {sampleActive && <SampleDataBanner onDismiss={onDismissSample} />}

      {pricesError && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 text-center">
          <p className="text-xs text-yellow-400">{pricesError}</p>
        </div>
      )}

      {/* Metal-feed notice — only for users who actually hold a metal. */}
      {metalError && holdings.some((h) => isMetal(h.coinId)) && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2 text-center">
          <p className="text-xs text-yellow-400">{metalError}</p>
        </div>
      )}

      <main className="px-8 py-6 pb-20">
        <Dashboard stats={stats} loading={pricesLoading} empty={showEmptyState} />

        <div
          className="mt-6 flex gap-2 overflow-x-auto"
          style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2.5 px-4 py-3 text-sm whitespace-nowrap flex-1 justify-center transition-colors"
                style={{
                  background: 'transparent',
                  borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                  marginBottom: '-0.5px',
                  color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                  fontWeight: isActive ? 500 : 400,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {activeTab === 'dashboard' && (
            showEmptyState ? (
              <EmptyDashboard
                transactions={transactions}
                goalOpenSignal={goalOpenSignal}
                onAddTransaction={() => setAddTxnOpen(true)}
                onExploreSample={onEnableSample}
                onSetGoal={handleSetGoal}
                onExploreMarket={() => setActiveTab('market')}
              />
            ) : (
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Left column — 60% */}
                <div className="flex-1 min-w-0 space-y-6">
                  <PortfolioLineChart
                    history={history}
                    transactions={transactions}
                    prices={prices}
                    totalValueUsd={walletScoped ? walletTotalUsd : stats.totalValue}
                    wallet={walletScoped ? { key: wallet.timelineWallet!, label: wallet.walletLabel! } : null}
                    lockedWalletLabel={walletLocked}
                  />
                  <HoldingsTable holdings={holdings} transactions={transactions} prices={prices} wallet={wallet} hideAllocation />
                  <ConnectedWalletsList
                    wallets={connectedWallets}
                    onRemove={removeWallet}
                    onSync={updateWallet}
                  />
                </div>

                {/* Right column — 40% */}
                <div className="w-full lg:w-[40%] flex-shrink-0 space-y-6">
                  <AllocationPieChart
                    holdings={walletScoped ? wallet.walletHoldings : holdings}
                    walletLabel={walletScoped ? wallet.walletLabel : null}
                    lockedWalletLabel={walletLocked}
                  />
                  <WalletAllocationChart holdings={holdings} transactions={transactions} />
                  <CashOutGoalCard transactions={transactions} />
                </div>
              </div>
            )
          )}

          {activeTab === 'transactions' && (
            <TransactionList
              transactions={transactions}
              holdings={holdings}
              metals={metalCoins}
              prices={prices}
              onAdd={addTransaction}
              onRemove={removeTransaction}
              onEdit={updateTransaction}
            />
          )}

          {activeTab === 'strategy' && <StrategyView holdings={holdings} />}

          {activeTab === 'tax' && <TaxView holdings={holdings} transactions={transactions} />}

          {activeTab === 'market' && <MarketView />}
        </div>
      </main>

      {addTxnOpen && (
        <AddTransactionModal
          onAdd={(tx) => { addTransaction(tx); setAddTxnOpen(false); }}
          metals={metalCoins}
          transactions={transactions}
          holdings={holdings}
          prices={prices}
          onClose={() => setAddTxnOpen(false)}
        />
      )}

      <Footer />
    </div>
  );
}

// ── App (outer) ───────────────────────────────────────────────────────────────

export default function App() {
  // On first render, fetch prices for the sample coins too if the user has no real
  // transactions and hasn't dismissed the demo — so sample P&L is live immediately.
  const [coinIds, setCoinIds] = useState<string[]>(() => {
    const real = getStoredCoinIds();
    if (real.length === 0 && isSampleEnabled()) return SAMPLE_COIN_IDS;
    return real;
  });

  const { prices: cryptoPrices, loading: pricesLoading, error: pricesError, lastUpdated, refresh } = useCoinPrices(
    coinIds.filter((id) => !isMetal(id) && !isStockOrETF(id))
  );
  const { prices: metalPrices, error: metalError } = useMetalPrices();
  const { prices: stockPrices } = useStockPrices(coinIds);

  const prices = useMemo(
    () => ({ ...cryptoPrices, ...metalPrices, ...stockPrices }),
    [cryptoPrices, metalPrices, stockPrices]
  );

  const metalCoins = useMemo<CoinListItem[]>(
    () => METALS.map((m) => ({ ...m, currentPrice: metalPrices[m.id]?.usd ?? 0 })),
    [metalPrices]
  );

  const { transactions, holdings, stats, history, addTransaction, removeTransaction, updateTransaction } = usePortfolio(prices);

  // Sample / demo mode: OPT-IN. Off by default (new users see the real empty
  // dashboard); enabled from the empty state, disabled via the banner Dismiss.
  const { active: sampleActive, enable: enableSample, disable: disableSample } = useSampleMode(transactions.length);

  // Sample portfolio computed on the fly from the same pipeline as real data.
  const sampleHoldings = useMemo(() => computeHoldings(SAMPLE_TRANSACTIONS, prices), [prices]);
  const sampleStats    = useMemo(() => computePortfolioStats(sampleHoldings), [sampleHoldings]);
  const sampleHistory  = useMemo(() => buildPortfolioHistory(SAMPLE_TRANSACTIONS, prices, []), [prices]);

  // What the dashboard actually renders: sample data when active, real data otherwise.
  const display = useMemo(
    () =>
      sampleActive
        ? { transactions: SAMPLE_TRANSACTIONS, holdings: sampleHoldings, stats: sampleStats, history: sampleHistory }
        : { transactions, holdings, stats, history },
    [sampleActive, sampleHoldings, sampleStats, sampleHistory, transactions, holdings, stats, history]
  );

  // Sample transactions are display-only — block edit/remove while sample mode is on.
  // (Adding still flows to real storage, which auto-ends sample mode.)
  const noop = useCallback(() => {}, []);
  const displayRemove = sampleActive ? noop : removeTransaction;
  const displayUpdate = sampleActive ? noop : updateTransaction;

  // Keep coinIds in sync: real coin IDs, or the sample set while sample mode is on.
  useEffect(() => {
    const realIds = Array.from(new Set(transactions.map((t) => t.coinId)));
    const effective = sampleActive ? SAMPLE_COIN_IDS : realIds;
    setCoinIds((prev) => {
      const prevKey = [...prev].sort().join(',');
      const nextKey = [...effective].sort().join(',');
      return prevKey === nextKey ? prev : effective;
    });
  }, [transactions, sampleActive]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
        <CurrencyProvider>
          <StrategyProvider>
            <PortfolioProvider holdings={display.holdings} stats={display.stats} transactions={display.transactions}>
              <UpgradePromptProvider>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/terms" element={<LegalPage doc="terms" />} />
                <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                <Route path="/disclaimer" element={<LegalPage doc="disclaimer" />} />
                <Route
                  path="/app"
                  element={
                    <RequireAuth>
                      <AppInner
                        holdings={display.holdings}
                        transactions={display.transactions}
                        prices={prices}
                        stats={display.stats}
                        history={display.history}
                        addTransaction={addTransaction}
                        removeTransaction={displayRemove}
                        updateTransaction={displayUpdate}
                        pricesLoading={pricesLoading}
                        pricesError={pricesError}
                        metalError={metalError}
                        lastUpdated={lastUpdated}
                        refresh={refresh}
                        metalCoins={metalCoins}
                        sampleActive={sampleActive}
                        onEnableSample={enableSample}
                        onDismissSample={disableSample}
                      />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/coin/:coinId"
                  element={
                    <RequireAuth>
                      <CoinChartPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequireAuth>
                      <SettingsPage />
                    </RequireAuth>
                  }
                />
              </Routes>
              </UpgradePromptProvider>
            </PortfolioProvider>
          </StrategyProvider>
        </CurrencyProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
